import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { BaseResponse } from '../models'

const _BASE_URL = import.meta.env.REACT_APP_INTEGRATIONS_SERVICE_API_BASE_URL!

export interface CheckpointRequest {
  accountId: string
  level: string
  connector: string
  direction: string
  object: string
  scheduling?: string
  payload?: { [key: string]: any }
}

export interface CheckpointUpdateRequest extends CheckpointRequest {
  active: boolean
}

export interface Checkpoint {
  active: boolean
  scheduling: string
}

export interface CreateSyncJobRequest {
  accountId: string
  level: string
  connector: string
  direction: string
  object: string
}

const toUrl = (request: CheckpointRequest): string => {
  return `/accounts/${request.accountId}/sync/auto/levels/${request.level}/connectors/${request.connector}/direction/${request.direction}/objects/${request.object}`
}

export const syncApi = createApi({
  reducerPath: 'syncApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['checkpoint'],
  endpoints: (builder) => ({
    getCheckpoint: builder.query<BaseResponse<Checkpoint | null>, CheckpointRequest>({
      query: (request) => {
        return {
          url: toUrl(request),
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      providesTags: ['checkpoint'],
    }),
    createCheckpoint: builder.mutation<BaseResponse<Checkpoint>, CheckpointRequest>({
      query: (request) => ({
        url: toUrl(request),
        method: 'POST',
        body: {
          active: true,
          ...(request.scheduling && { scheduling: request.scheduling }),
          ...(request.payload && { payload: request.payload }),
        },
      }),
      invalidatesTags: ['checkpoint'],
    }),
    updateCheckpoint: builder.mutation<BaseResponse<Checkpoint>, CheckpointUpdateRequest>({
      query: (request) => ({
        url: toUrl(request),
        method: 'PUT',
        body: {
          active: request.active,
        },
      }),
      invalidatesTags: ['checkpoint'],
    }),
    deleteCheckpoint: builder.mutation<void, CheckpointRequest>({
      query: (request) => ({
        url: toUrl(request),
        method: 'DELETE',
      }),
      invalidatesTags: ['checkpoint'],
    }),
    createSyncJob: builder.mutation<void, CreateSyncJobRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/sync`,
        method: 'POST',
        body: request,
      }),
    }),
  }),
})

export const { useGetCheckpointQuery, useCreateCheckpointMutation, useUpdateCheckpointMutation, useDeleteCheckpointMutation, useCreateSyncJobMutation } =
  syncApi
